import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Star, Zap, ShieldCheck, Landmark, Clock,
  Users2, ShoppingCart, X, Check, Info, ArrowRight, Filter, DollarSign, Ticket, Gift,
  GraduationCap, TrendingUp, HelpCircle, BookOpen, AlertTriangle,
} from "lucide-react";

/**
 * Client-side Tradeline Marketplace (preview mode — payment not connected).
 * Pulls from /api/tradelines and lets the client reserve 1–3 lines.
 */
export default function ClientTradelines() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [recommendedIds, setRecommendedIds] = useState([]);
  const [bestValueIds, setBestValueIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterTier, setFilterTier] = useState(null);
  const [filterBank, setFilterBank] = useState("");
  const [reportingSoonOnly, setReportingSoonOnly] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showLearn, setShowLearn] = useState(false);
  const [busy, setBusy] = useState(false);
  // Promo code state — persisted for the session so a valid code carries into checkout.
  const [promo, setPromo] = useState(() => {
    try {
      const raw = sessionStorage.getItem("cq_tradeline_promo");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [promoCelebrate, setPromoCelebrate] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tradelines");
      setItems(data?.items || []);
      setTiers(data?.tiers || [150, 250, 350, 500]);
      setRecommendedIds(data?.recommended_ids || []);
      setBestValueIds(data?.best_value_ids || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load tradelines");
    }
  };
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const banks = useMemo(() => [...new Set(items.map((i) => i.bank))].sort(), [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (filterTier) list = list.filter((i) => i.retail_price === filterTier);
    if (filterBank) list = list.filter((i) => i.bank === filterBank);
    if (reportingSoonOnly) list = list.filter((i) => i.reports_soon);
    return list;
  }, [items, filterTier, filterBank, reportingSoonOnly]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 3) {
        toast.error("Maximum 3 tradelines per reservation.");
        return prev;
      }
      return [...prev, id];
    });
  };

  // Preview pricing helper — mirrors the server's _apply_promo() logic.
  const adjustedPrice = (t) => {
    if (!t) return 0;
    let price = t.retail_price;
    if (promo) {
      if (promo.waive_rush && t.is_rush) price = t.base_price;
      if (promo.discount_pct) price = Math.round(price * (1 - promo.discount_pct / 100));
    }
    return price;
  };

  const fireConfetti = () => {
    const defaults = { spread: 70, ticks: 60, gravity: 0.9, decay: 0.94, startVelocity: 30, colors: ["#F59E0B", "#FBBF24", "#FEF3C7", "#EAB308", "#FFFFFF"] };
    confetti({ ...defaults, particleCount: 60, origin: { x: 0.15, y: 0.6 } });
    confetti({ ...defaults, particleCount: 60, origin: { x: 0.85, y: 0.6 } });
    setTimeout(() => confetti({ ...defaults, particleCount: 90, origin: { x: 0.5, y: 0.55 } }), 180);
  };

  const applyPromo = (p) => {
    setPromo(p);
    try { sessionStorage.setItem("cq_tradeline_promo", JSON.stringify(p)); } catch {}
    setPromoCelebrate(true);
    fireConfetti();
  };
  const clearPromo = () => {
    setPromo(null);
    try { sessionStorage.removeItem("cq_tradeline_promo"); } catch {}
  };

  const originalTotal = useMemo(() => selectedIds.reduce((s, id) => s + (byId[id]?.retail_price || 0), 0), [selectedIds, byId]);
  const total = useMemo(() => selectedIds.reduce((s, id) => s + adjustedPrice(byId[id]), 0), [selectedIds, byId, promo]);

  const reserve = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/tradelines/reserve", { tradeline_ids: selectedIds, promo_code: promo?.code || "" });
      toast.success(promo ? `Reserved with ${promo.label} — starting checkout…` : "Reserved — starting checkout…");
      setSelectedIds([]);
      setShowCompare(false);
      try { sessionStorage.removeItem("cq_tradeline_promo"); } catch {}
      // Kick off Stripe Checkout immediately so the client pays in the same flow.
      try {
        const { data: co } = await api.post(`/tradelines/reservations/${res.id}/checkout`, {
          origin_url: window.location.origin,
        });
        if (co?.checkout_url) {
          window.location.href = co.checkout_url;
          return;
        }
      } catch (payErr) {
        toast.error(formatApiError(payErr.response?.data?.detail) || "Checkout couldn't start — pay from your reservations page.");
      }
      nav("/portal/tradelines/reservations");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reservation failed");
    } finally { setBusy(false); }
  };

  const recServices = recommendedIds.map((id) => byId[id]).filter(Boolean);

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <div className="flex flex-col items-center sm:items-start sm:flex-row sm:justify-between gap-4 text-center sm:text-left">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Client portal · Tradelines</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">
              Tradeline Marketplace
            </h1>
            <p className="text-slate-600 mt-2 max-w-2xl mx-auto sm:mx-0">
              Authorized-user tradelines from vetted wholesale partners. Pick up to 3, reserve now,
              and we'll finish payment together — no charge until you approve.
            </p>
          </div>
          <div className="w-full sm:w-auto flex items-center justify-center sm:justify-end gap-2 flex-wrap">
            <PromoField promo={promo} onApply={applyPromo} onClear={clearPromo} />
            <Button
              variant="outline"
              className="border-slate-200"
              onClick={() => nav("/portal/tradelines/reservations")}
              data-testid="tl-my-reservations"
            >
              My Reservations
            </Button>
            <Button
              onClick={() => setShowLearn(true)}
              className="relative bg-slate-900 text-amber-200 hover:text-white hover:bg-slate-800 shadow-lg shadow-slate-900/20 ring-2 ring-amber-400/40 hover:ring-amber-400/70 transition"
              data-testid="tl-learn-open"
            >
              <GraduationCap size={14} className="mr-1" /> What is a Tradeline?
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-400 animate-ping" />
              <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-400" />
            </Button>
            <Button
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
              onClick={() => setShowQuiz(true)}
              data-testid="tl-quiz-open"
            >
              <Sparkles size={14} className="mr-1" /> Help Me Choose
            </Button>
          </div>
        </div>

        {/* Education banner — condensed on mobile (single tappable pill), full card on ≥sm */}
        {/* Mobile pill */}
        <button
          onClick={() => setShowLearn(true)}
          className="sm:hidden mt-4 w-full flex items-center gap-3 rounded-full border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50 pl-2 pr-4 py-2 shadow-sm active:scale-[0.99] transition"
          data-testid="tl-learn-banner-mobile"
        >
          <span className="h-8 w-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow shrink-0">
            <BookOpen size={14} />
          </span>
          <span className="flex-1 min-w-0 text-left">
            <span className="text-[9px] uppercase tracking-[0.2em] font-black text-amber-700 block leading-none">New here?</span>
            <span className="text-[13px] font-black text-slate-900 leading-tight">What is a tradeline?</span>
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black text-slate-900 bg-white rounded-full px-2.5 py-1 border border-slate-200 shrink-0">
            60s <ArrowRight size={11} />
          </span>
        </button>

        {/* Desktop / tablet full card */}
        <div className="hidden sm:flex mt-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50 p-5 items-start gap-3 shadow-sm">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow shrink-0">
            <BookOpen size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-700">New here?</p>
            <h3 className="font-display font-black text-lg text-slate-900 leading-tight mt-0.5">
              Learn what a tradeline is before you buy — in 60 seconds.
            </h3>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Tradelines aren't loans — you're added as an <b>authorized user</b> on someone else's aged credit card,
              so their long payment history and low utilization show up on your report.
              Understand what actually posts, what doesn't, and pick the right line for <b>your</b> funding goal.
            </p>
          </div>
          <Button
            onClick={() => setShowLearn(true)}
            className="bg-slate-900 text-white hover:bg-slate-800 shrink-0"
            data-testid="tl-learn-banner"
          >
            <GraduationCap size={13} className="mr-1" /> Learn how it works
          </Button>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFilterTier(null)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border ${!filterTier ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
          >
            All tiers ({items.length})
          </button>
          {tiers.map((t) => (
            <button
              key={t}
              onClick={() => setFilterTier(t === filterTier ? null : t)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${filterTier === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}
              data-testid={`tl-tier-${t}`}
            >
              ${t}
            </button>
          ))}
          <select
            value={filterBank}
            onChange={(e) => setFilterBank(e.target.value)}
            className="h-9 rounded-md border border-slate-200 px-3 text-sm"
            data-testid="tl-bank-filter"
          >
            <option value="">All banks</option>
            {banks.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <button
            onClick={() => setReportingSoonOnly((v) => !v)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border ${reportingSoonOnly ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-slate-700 border-slate-200"}`}
            data-testid="tl-reports-soon"
          >
            <Clock size={12} /> Reporting soon
          </button>
        </div>

        {/* Cart bar — sticky top on ≥sm, floating bottom bar (above bottom nav) on mobile */}
        {selectedIds.length > 0 && (
          <div
            className="fixed left-3 right-3 z-40 rounded-2xl border border-amber-200 bg-white/95 backdrop-blur px-3 py-2.5 shadow-2xl
                       bottom-[calc(env(safe-area-inset-bottom,0px)+72px)]
                       sm:static sm:mt-4 sm:sticky sm:top-16 sm:z-30 sm:px-4 sm:py-3 sm:left-auto sm:right-auto sm:rounded-2xl sm:shadow-lg"
            data-testid="tl-cart"
          >
            {/* Mobile compact row */}
            <div className="flex items-center gap-3 sm:hidden">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shrink-0">
                <ShoppingCart size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 leading-none">
                  {selectedIds.length} in cart
                </p>
                <p className="text-base font-black leading-tight mt-0.5">
                  {promo && originalTotal !== total ? (
                    <>
                      <span className="line-through text-slate-400 mr-1 text-sm">${originalTotal}</span>
                      <span className="text-amber-600">${total}</span>
                    </>
                  ) : (
                    <span className="text-amber-600">${total}</span>
                  )}
                </p>
              </div>
              <Button
                onClick={() => setShowCompare(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
                data-testid="tl-reserve-open-mobile"
              >
                Review <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>

            {/* Desktop / tablet full row */}
            <div className="hidden sm:flex items-center gap-3 flex-wrap">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center">
                <ShoppingCart size={16} />
              </div>
              <p className="text-sm font-bold text-slate-800">
                {selectedIds.length} reserved ·{" "}
                {promo && originalTotal !== total ? (
                  <>
                    <span className="line-through text-slate-400 mr-1">${originalTotal}</span>
                    <span className="text-amber-600">${total}</span>
                  </>
                ) : (
                  <span className="text-amber-600">${total}</span>
                )}
              </p>
              <div className="flex-1 min-w-0 overflow-x-auto flex gap-2">
                {selectedIds.map((id) => {
                  const t = byId[id];
                  if (!t) return null;
                  const adj = adjustedPrice(t);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 text-xs font-bold whitespace-nowrap">
                      {t.bank} ${t.credit_limit.toLocaleString()} — ${adj}
                      <button onClick={() => toggle(id)} className="ml-1 text-amber-600"><X size={12} /></button>
                    </span>
                  );
                })}
              </div>
              <Button variant="outline" onClick={() => setShowCompare(true)} data-testid="tl-compare-open">Compare</Button>
              <Button
                onClick={() => setShowCompare(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="tl-reserve-open"
              >
                Reserve <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Recommended row */}
        {recServices.length > 0 && !filterTier && !filterBank && !reportingSoonOnly && (
          <section className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-amber-500" />
              <h2 className="font-display text-2xl font-black text-slate-900">Recommended for You</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recServices.map((t) => (
                <TradelineCard key={t.id} t={t} selected={selectedIds.includes(t.id)} onToggle={toggle}
                  featured={recommendedIds.includes(t.id)}
                  isBestValue={bestValueIds.includes(t.id)}
                  promo={promo} adjustedPrice={adjustedPrice(t)} />
              ))}
            </div>
          </section>
        )}

        {/* All / filtered grid */}
        <section className="mt-8">
          <h2 className="font-display text-2xl font-black text-slate-900 mb-3">
            {filterTier ? `$${filterTier} tier` : filterBank ? filterBank : reportingSoonOnly ? "Reporting soon" : "All tradelines"}
            <span className="ml-2 text-sm text-slate-400 font-semibold">{filtered.length}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tl-grid">
            {filtered.map((t) => (
              <TradelineCard key={t.id} t={t} selected={selectedIds.includes(t.id)} onToggle={toggle}
                featured={recommendedIds.includes(t.id)}
                isBestValue={bestValueIds.includes(t.id)}
                promo={promo} adjustedPrice={adjustedPrice(t)} />
            ))}
          </div>
          {filtered.length === 0 && (
            <Card className="p-10 rounded-2xl text-center text-sm text-slate-400 border-slate-100">
              No tradelines match those filters right now — check back after our next 3-day sync.
            </Card>
          )}
        </section>

        <Disclosures />
      </div>

      {showQuiz && <QuizModal onClose={() => setShowQuiz(false)} tiers={tiers} banks={banks}
        onResult={(ids) => { setRecommendedIds(ids); setShowQuiz(false); toast.success(`Recommended ${ids.length} tradelines`); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}

      {showCompare && (
        <ReserveModal
          onClose={() => setShowCompare(false)}
          items={selectedIds.map((id) => byId[id]).filter(Boolean)}
          total={total}
          originalTotal={originalTotal}
          promo={promo}
          adjustedPrice={adjustedPrice}
          busy={busy}
          onConfirm={reserve}
        />
      )}

      {promoCelebrate && promo && (
        <PromoCelebration promo={promo} onClose={() => setPromoCelebrate(false)} />
      )}

      {showLearn && <LearnModal onClose={() => setShowLearn(false)} />}
    </AppShell>
  );
}

function TradelineCard({ t, selected, onToggle, featured, isBestValue, promo, adjustedPrice }) {
  const showDiscount = promo && adjustedPrice !== t.retail_price;
  return (
    <Card
      className={`p-4 rounded-2xl border transition ${selected ? "border-amber-400 bg-amber-50/60 shadow-md" : "border-slate-100 hover:border-slate-200 hover:shadow-sm"}`}
      data-testid={`tl-card-${t.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-[14px] bg-gradient-to-br from-slate-900 to-slate-700 text-white flex items-center justify-center shadow relative overflow-hidden">
          <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent" />
          <Landmark size={20} className="relative z-10" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 flex flex-wrap gap-1">
            {featured && <span className="text-amber-600">RECOMMENDED</span>}
            {isBestValue && <span className="text-emerald-600">· BEST VALUE</span>}
            {t.is_rush && !promo?.waive_rush && <span className="text-rose-600 inline-flex items-center gap-0.5"><Zap size={10} /> RUSH · +${t.rush_premium}</span>}
            {t.is_rush && promo?.waive_rush && <span className="text-emerald-600 inline-flex items-center gap-0.5"><Gift size={10} /> RUSH WAIVED</span>}
            <span className="text-slate-500">· AU Tradeline</span>
          </p>
          <h3 className="font-display font-black text-lg text-slate-900 leading-tight mt-0.5">
            {t.bank} · ${t.credit_limit.toLocaleString()}
          </h3>
        </div>
        <div className="text-right">
          {showDiscount ? (
            <>
              <p className="font-display text-2xl font-black text-amber-600">${adjustedPrice}</p>
              <p className="text-[10px] font-bold text-slate-400 line-through">${t.retail_price}</p>
            </>
          ) : (
            <>
              <p className="font-display text-2xl font-black text-slate-900">${t.retail_price}</p>
              {t.is_rush && (
                <p className="text-[10px] text-slate-400">
                  ${t.base_price} + ${t.rush_premium} rush
                </p>
              )}
            </>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
        <div><span className="text-slate-400">Age</span>: <b>{t.account_age_years}yr</b> ({t.opened_year})</div>
        <div><span className="text-slate-400">Util</span>: <b>{t.utilization_pct}%</b></div>
        <div className="col-span-2">
          <span className="text-slate-400">Posts</span>:{" "}
          <b className={t.is_rush ? "text-rose-700" : "text-slate-800"}>
            {t.posting_window_label || t.reporting_period}
          </b>
          {t.is_rush && <span className="ml-1 text-[10px] font-black text-rose-600">· {t.days_from_close}d after close</span>}
        </div>
        <div className="col-span-2"><span className="text-slate-400">Spots</span>: <b>{t.spots_available} available</b></div>
      </div>
      <button
        onClick={() => onToggle(t.id)}
        className={`mt-3 w-full inline-flex items-center justify-center gap-1 rounded-lg px-3 py-2 text-sm font-bold transition ${
          selected ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-slate-900 text-white hover:bg-slate-800"
        }`}
        data-testid={`tl-add-${t.id}`}
      >
        {selected ? <><Check size={14} /> Added to cart</> : <>Reserve</>}
      </button>
    </Card>
  );
}

function ReserveModal({ items, total, originalTotal, promo, adjustedPrice, onClose, onConfirm, busy }) {
  const savings = (originalTotal || 0) - (total || 0);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tl-reserve-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-black text-amber-600">Reserve tradelines</p>
            <h3 className="font-display text-2xl font-black text-slate-900 mt-1">Order summary</h3>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-800"><X size={18} /></button>
        </div>
        {promo && (
          <div className="mt-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-300 px-4 py-2 flex items-center gap-2">
            <Gift size={14} className="text-amber-600" />
            <p className="text-xs font-black text-amber-800 uppercase tracking-widest">
              {promo.label} · code <span className="font-mono">{promo.code}</span>
            </p>
          </div>
        )}
        <div className="mt-4 space-y-3">
          {items.map((t) => {
            const adj = adjustedPrice ? adjustedPrice(t) : t.retail_price;
            const cut = adj !== t.retail_price;
            return (
              <div key={t.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
                <div>
                  <p className="font-bold text-slate-900">{t.bank} · ${t.credit_limit.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{t.account_age_years}yr · {t.utilization_pct}% util · {t.reporting_period}</p>
                  <p className={`text-xs font-bold mt-0.5 ${t.is_rush && !promo?.waive_rush ? "text-rose-700" : "text-slate-700"}`}>
                    {t.posting_window_label}
                    {t.is_rush && promo?.waive_rush ? " · Rush waived" : t.is_rush ? " · Rush posting" : ""}
                  </p>
                </div>
                <div className="text-right">
                  {cut ? (
                    <>
                      <p className="font-display text-xl font-black text-amber-600">${adj}</p>
                      <p className="text-[10px] font-bold text-slate-400 line-through">${t.retail_price}</p>
                    </>
                  ) : (
                    <>
                      <p className="font-display text-xl font-black text-slate-900">${t.retail_price}</p>
                      {t.is_rush && <p className="text-[10px] text-slate-400">${t.base_price} + ${t.rush_premium} rush</p>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {promo && savings > 0 && (
            <div className="flex items-center justify-between px-1 text-sm">
              <p className="text-slate-500">Subtotal</p>
              <p className="text-slate-500 line-through">${originalTotal}</p>
            </div>
          )}
          {promo && savings > 0 && (
            <div className="flex items-center justify-between px-1 text-sm">
              <p className="text-emerald-700 font-bold">{promo.label} discount</p>
              <p className="text-emerald-700 font-bold">−${savings}</p>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <p className="text-sm font-bold text-slate-700">Total</p>
            <p className="font-display text-2xl font-black text-amber-600">${total}</p>
          </div>
        </div>
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 leading-relaxed">
          <b>Secure Stripe Checkout</b> — click confirm to pay ${total} and lock in your reservation.
          Non-refundable once processed.
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" data-testid="tl-reserve-confirm">
            {busy ? "Redirecting…" : `Reserve & Pay $${total}`}
          </Button>
        </div>
      </Card>
    </div>,
    document.body
  );
}

function QuizModal({ onClose, onResult, tiers, banks }) {
  const [state, setState] = useState({ goal: "", time_horizon: "", budget_tier: "any", preferred_bank: "", utilization_matters: null });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tradelines/help-me-choose", state);
      onResult(data.recommended_ids || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Try again");
    } finally { setBusy(false); }
  };
  const set = (k, v) => setState((p) => ({ ...p, [k]: v }));
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="tl-quiz-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest font-black text-amber-600">Help Me Choose</p>
            <h3 className="font-display text-2xl font-black text-slate-900 mt-1">Find the right tradelines</h3>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-800"><X size={18} /></button>
        </div>
        <div className="mt-4 space-y-4">
          <Row label="What are you trying to accomplish?">
            <Input value={state.goal} onChange={(e) => set("goal", e.target.value)} placeholder="e.g. Prep for a business LOC in 45 days" />
          </Row>
          <Row label="Time horizon">
            <div className="flex flex-wrap gap-2">
              {["ASAP","30 days","60-90 days","No rush"].map((v) => (
                <button key={v} onClick={() => set("time_horizon", v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.time_horizon === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>{v}</button>
              ))}
            </div>
          </Row>
          <Row label="Budget">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => set("budget_tier", "any")} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.budget_tier === "any" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Any</button>
              {tiers.map((t) => (
                <button key={t} onClick={() => set("budget_tier", String(t))} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.budget_tier === String(t) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>Up to ${t}</button>
              ))}
            </div>
          </Row>
          <Row label="Preferred bank (optional)">
            <select value={state.preferred_bank} onChange={(e) => set("preferred_bank", e.target.value)} className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm">
              <option value="">Any bank</option>
              {banks.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Row>
          <Row label="Do you want the lowest utilization first?">
            <div className="flex gap-2">
              {[[true,"Yes"],[false,"No"]].map(([v,l]) => (
                <button key={l} onClick={() => set("utilization_matters", v)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${state.utilization_matters === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>{l}</button>
              ))}
            </div>
          </Row>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="tl-quiz-submit">
            <Sparkles size={13} className="mr-1" /> Recommend
          </Button>
        </div>
      </Card>
    </div>,
    document.body
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <p className="text-xs font-bold text-slate-700 mb-1.5">{label}</p>
      {children}
    </label>
  );
}

function PromoField({ promo, onApply, onClear }) {
  const [value, setValue] = useState("");
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    const code = value.trim();
    if (!code) return;
    setChecking(true); setError("");
    try {
      const { data } = await api.get(`/tradelines/promo/validate`, { params: { code } });
      onApply(data);
      setValue("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Invalid code");
    } finally { setChecking(false); }
  };

  if (promo) {
    return (
      <div
        data-testid="tl-promo-active"
        className="inline-flex items-center gap-2 rounded-full pl-3 pr-1 py-1 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 text-slate-900 shadow-md border border-amber-500/60"
      >
        <Gift size={13} className="text-amber-700" />
        <span className="text-[11px] font-black uppercase tracking-widest">
          {promo.label} <span className="font-mono">· {promo.code}</span>
        </span>
        <button
          onClick={onClear}
          data-testid="tl-promo-clear"
          className="h-6 w-6 rounded-full bg-white/70 hover:bg-white text-slate-700 grid place-items-center"
          title="Remove code"
        >
          <X size={12} />
        </button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="inline-flex items-stretch overflow-hidden rounded-full border border-slate-200 bg-white shadow-sm hover:border-amber-300 transition">
      <div className="pl-3 pr-1 flex items-center text-slate-400"><Ticket size={13} /></div>
      <input
        value={value}
        onChange={(e) => { setValue(e.target.value.toUpperCase()); setError(""); }}
        placeholder="Promo code"
        className="w-28 h-8 text-xs font-bold tracking-widest bg-transparent outline-none uppercase placeholder:text-slate-300 placeholder:font-normal placeholder:tracking-normal"
        data-testid="tl-promo-input"
      />
      <button
        type="submit"
        disabled={checking || !value.trim()}
        className="px-3 text-[11px] font-black uppercase tracking-widest bg-slate-900 text-white hover:bg-amber-500 hover:text-slate-900 transition disabled:opacity-50"
        data-testid="tl-promo-apply"
      >
        {checking ? "…" : "Apply"}
      </button>
      {error && <span className="sr-only" role="alert">{error}</span>}
    </form>
  );
}

function PromoCelebration({ promo, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3800);
    return () => clearTimeout(t);
  }, [onClose]);
  const perks = [];
  if (promo.discount_pct) perks.push(`${promo.discount_pct}% off every tradeline`);
  if (promo.waive_rush) perks.push("Rush fees waived");
  return createPortal(
    <div
      className="fixed inset-0 z-[110] pointer-events-none flex items-center justify-center px-6"
      data-testid="tl-promo-celebrate"
    >
      <div
        className="relative pointer-events-auto max-w-md w-full rounded-3xl overflow-hidden shadow-2xl border border-amber-300/60 animate-[cq-promo-in_0.5s_cubic-bezier(0.16,1,0.3,1)]"
        onClick={onClose}
      >
        {/* Gold shimmer */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-yellow-300 to-amber-500" />
        <div className="absolute inset-0 opacity-70"
             style={{ background: "linear-gradient(115deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 60%)",
                      backgroundSize: "200% 100%",
                      animation: "cq-shimmer 2.2s linear infinite" }} />
        <div className="relative p-7 text-slate-900">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-slate-900 text-amber-300 grid place-items-center shadow-lg">
              <Gift size={18} />
            </div>
            <p className="text-xs font-black uppercase tracking-[0.28em] text-slate-900">You unlocked</p>
          </div>
          <h3 className="font-display text-3xl font-black leading-tight mt-2 drop-shadow-sm">
            {promo.label}
          </h3>
          <p className="text-sm text-slate-800 mt-1">
            Code <span className="font-mono font-black">{promo.code}</span> applied to every tradeline in your cart.
          </p>
          <ul className="mt-3 space-y-1">
            {perks.map((p) => (
              <li key={p} className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <Check size={14} className="text-emerald-700" /> {p}
              </li>
            ))}
          </ul>
          <button
            onClick={onClose}
            className="mt-5 inline-flex items-center gap-1 rounded-full bg-slate-900 text-amber-200 hover:text-white px-4 py-1.5 text-xs font-black uppercase tracking-widest"
            data-testid="tl-promo-celebrate-close"
          >
            Keep shopping <ArrowRight size={12} />
          </button>
        </div>
      </div>
      <style>{`
        @keyframes cq-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes cq-promo-in { from{opacity:0;transform:scale(0.9) translateY(20px)} to{opacity:1;transform:scale(1) translateY(0)} }
      `}</style>
    </div>,
    document.body
  );
}

function Disclosures() {
  return (
    <Card className="mt-8 p-5 rounded-2xl border-slate-100 bg-slate-50" data-testid="tl-disclosures">
      {/* 14-day post guarantee — front and center */}
      <div className="mb-4 rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow shrink-0">
          <ShieldCheck size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.28em] font-black text-emerald-700">14-Day Post Guarantee</p>
          <h4 className="font-display font-black text-lg text-slate-900 leading-tight mt-0.5">
            Posts within 14 days of the posting date — or we make it right.
          </h4>
          <p className="text-[13px] text-slate-700 mt-1 leading-relaxed">
            Every tradeline you buy through us is guaranteed to hit your credit report within <b>14 days of the
            listed posting date</b>. If a line misses that window, you get a <b>full refund</b> or a swap onto a
            <b> comparable tradeline at no extra cost</b> — your call.
          </p>
          <p className="text-[11px] text-slate-500 mt-2 italic">
            Reporting timing is ultimately controlled by the issuing bank and our wholesale vendors — we can't
            force bureau updates. This guarantee is how we take that risk off you.
          </p>
        </div>
      </div>

      <p className="text-xs uppercase tracking-widest font-black text-slate-500 mb-2 flex items-center gap-1">
        <Info size={12} /> Important disclosures
      </p>
      <ul className="text-[12px] text-slate-600 space-y-1 list-disc pl-5">
        <li>This is an <b>authorized-user tradeline</b>. You do not own the underlying account or the credit limit.</li>
        <li>Credit-score changes, funding approvals and lending outcomes are <b>not guaranteed</b> — only the posting timing is (see above).</li>
        <li>Results depend on your complete credit profile and each bureau's reporting behavior.</li>
        <li>The same tradeline is priced the same for every client — pricing is never based on your credit profile or perceived ability to pay.</li>
      </ul>
    </Card>
  );
}

function LearnModal({ onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <Card
        className="max-w-3xl w-full rounded-3xl overflow-hidden my-6 sm:my-0"
        onClick={(e) => e.stopPropagation()}
        data-testid="tl-learn-modal"
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
          <div className="absolute inset-0 opacity-30" style={{
            backgroundImage: "radial-gradient(circle at 20% 20%, rgba(245,158,11,0.35), transparent 50%), radial-gradient(circle at 80% 80%, rgba(251,191,36,0.25), transparent 50%)",
          }} />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-300">Credit Quack Academy</p>
              <h3 className="font-display text-3xl sm:text-4xl font-black leading-tight mt-1">
                What is a Tradeline?
              </h3>
              <p className="text-slate-300 text-sm mt-1">The 60-second, no-fluff guide.</p>
            </div>
            <button onClick={onClose} className="p-1 text-slate-300 hover:text-white" data-testid="tl-learn-close">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Intro */}
          <section>
            <p className="text-slate-700 leading-relaxed">
              A <b>tradeline</b> is any account that shows up on your credit report — credit cards, auto loans,
              mortgages, personal loans, etc. When people say "buy a tradeline," they specifically mean being
              added as an <b>Authorized User (AU)</b> onto someone else's <i>seasoned</i>, well-managed credit card.
              Their long payment history and low utilization then show up on your report as if it were your own history.
            </p>
          </section>

          {/* Why it matters */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={16} className="text-amber-500" />
              <h4 className="font-display font-black text-lg text-slate-900">Why this matters for your funding journey</h4>
            </div>
            <ul className="text-sm text-slate-700 space-y-2 pl-1">
              <li className="flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <span><b>Length of credit history</b> is ~15% of your FICO score. Adding an aged AU tradeline can raise your average age of accounts overnight.</span></li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <span><b>Utilization</b> is ~30% of your FICO. A big-limit, low-utilization AU line pushes your overall utilization down.</span></li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <span><b>Payment history</b> is ~35% of your FICO. Years of on-time payments now stack behind your name.</span></li>
              <li className="flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                <span>Used before applying for a mortgage, auto loan, business LOC, or high-tier credit card — timed to post before you apply.</span></li>
            </ul>
          </section>

          {/* Account types */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Landmark size={16} className="text-amber-500" />
              <h4 className="font-display font-black text-lg text-slate-900">The three types of tradelines</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
                <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700">AU · What we sell</p>
                <p className="font-display font-black text-slate-900 mt-1">Authorized User</p>
                <p className="text-xs text-slate-600 mt-1">You piggyback on someone else's aged card. You don't own it. History posts to your report. No new account is opened in your name.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">Primary</p>
                <p className="font-display font-black text-slate-900 mt-1">Your own account</p>
                <p className="text-xs text-slate-600 mt-1">Credit cards, loans, and mortgages you personally applied for and are legally liable for. Builds credit long-term.</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-widest font-black text-slate-500">Joint</p>
                <p className="font-display font-black text-slate-900 mt-1">Shared responsibility</p>
                <p className="text-xs text-slate-600 mt-1">Two people both liable — think co-signed mortgages or joint credit cards. Both credit reports report the account fully.</p>
              </div>
            </div>
          </section>

          {/* What does & doesn't happen */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Info size={16} className="text-amber-500" />
              <h4 className="font-display font-black text-lg text-slate-900">What actually happens</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                <p className="font-bold text-emerald-800 mb-1">✅ What you get</p>
                <ul className="text-slate-700 space-y-1 list-disc pl-4">
                  <li>The full account history posts to your credit report (usually within 30 days)</li>
                  <li>You keep the tradeline on your report for 1–2 statement cycles</li>
                  <li>Your utilization, avg age of accounts, and payment history all get better inputs</li>
                </ul>
              </div>
              <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3">
                <p className="font-bold text-rose-800 mb-1">🚫 What you don't get</p>
                <ul className="text-slate-700 space-y-1 list-disc pl-4">
                  <li>No physical card, no access to funds, no online account access</li>
                  <li>No liability for the debt (that's the primary cardholder's)</li>
                  <li>No guarantee of a score bump — every credit profile is different</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Timing */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-amber-500" />
              <h4 className="font-display font-black text-lg text-slate-900">Timing is everything</h4>
            </div>
            <p className="text-sm text-slate-700">
              Every tradeline has a <b>statement close date</b> and a <b>reporting window</b>. Lines that post
              within a few days of statement close (marked <span className="text-rose-700 font-bold">RUSH</span>)
              get you results faster — that's why they carry a small rush premium. If you have a lender
              application coming up, tell us the date and we'll match you to a line that posts before it.
              <span className="block mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                💡 Pro tip: our <b>Help Me Choose</b> quiz asks your time horizon and auto-filters lines that post in time.
              </span>
            </p>
          </section>

          {/* Legal */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-amber-500" />
              <h4 className="font-display font-black text-lg text-slate-900">Is this legit?</h4>
            </div>
            <p className="text-sm text-slate-700">
              Yes. Adding an authorized user is a feature every major U.S. credit card issuer offers.
              Paying for that service is legal. We only work with vetted wholesale partners who follow
              CROA guidelines, use secured supplier portals, and never share your identity beyond what's
              required to add you to a card. <b>We never claim you'll be approved for any loan or funding
              — a stronger credit report just gives you a better shot.</b>
            </p>
          </section>

          {/* CTA */}
          <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>Got it</Button>
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            >
              <Sparkles size={13} className="mr-1" /> Ready to shop
            </Button>
          </div>
        </div>
      </Card>
    </div>,
    document.body
  );
}
